import { EventSeverity, type MediaEvent, type MediaRequest, MediaType } from '@cobliteam/common-types/src';
import { useGetMediaView } from '@cobliteam/hooks/src/useCamera/useCameraMedia';
import { useTranslation } from '@cobliteam/i18n';
import { Skeleton, Tooltip, Typography } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { format } from '@cobliteam/utils/date';
import { getMediaEventLabel } from '../../../../../utils/mediaUtils';
import TagEventSeverity from '../../../../Tag/TagEventSeverity';

export interface DialogMediaHeaderProps {
  mediaId?: string;
}

const mediaTypeLabels: Record<MediaType, string> = {
  [MediaType.EVENT_VIDEO]: 'Vídeo de evento',
  [MediaType.FOOTAGE_VIDEO]: 'Vídeo de um trecho',
  [MediaType.TIMELAPSE]: 'Resumo do trajeto',
};

const DialogMediaHeader = ({ mediaId }: DialogMediaHeaderProps) => {
  const { data: mediaView } = useGetMediaView(mediaId);
  const { t } = useTranslation('media');
  if (!mediaView?.mediaInfo) {
    return <Skeleton className='h-5 w-80 bg-gray-5' />;
  }

  const { mediaInfo } = mediaView;
  const { mediaType, severity = EventSeverity.HIGH, mediaDetails } = mediaInfo;

  const isEvent = mediaType === MediaType.EVENT_VIDEO;

  const renderEventHeader = ({ eventTrigger }: MediaEvent) => (
    <div className='flex space-x-1'>
      <Typography variant='text-lg-semibold'>{getMediaEventLabel(eventTrigger, t)}</Typography>
      <TagEventSeverity severity={severity} />
    </div>
  );

  const renderMediaRequestHeader = ({ createdAt, createdByTrigger }: MediaRequest) => {
    const formattedDate = createdAt ? format(createdAt, 'dd MMM yyyy HH:mm:ss') : 'Data não disponível';

    return (
      <div className='flex items-center gap-2'>
        <Typography variant='text-lg-semibold'>{`Solicitado em: ${formattedDate}`}</Typography>
        <Tooltip
          content={
            <>
              {mediaTypeLabels[mediaType] && (
                <div>
                  Categoria do vídeo: <b>{mediaTypeLabels[mediaType]}</b>
                </div>
              )}
              {createdByTrigger?.name && (
                <div>
                  Regra solicitante: <b>{createdByTrigger.name}</b>
                </div>
              )}
            </>
          }
        >
          <Icon name='info' />
        </Tooltip>
      </div>
    );
  };

  return isEvent ? renderEventHeader(mediaDetails as MediaEvent) : renderMediaRequestHeader(mediaDetails as MediaRequest);
};

export default DialogMediaHeader;
